import React from 'react';
import { Box, Paper, Button, Typography, Container } from '@mui/material';

import { CalendarBoxStyles } from '../styles';

export interface ActionButton {
  label: string;
  onClick: () => void | Promise<void>;
  variant?: 'contained' | 'outlined' | 'text';
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  disabled?: boolean;
  loading?: boolean;
  startIcon?: React.ReactNode;
  sx?: object;
  allowNavigation?: boolean;
}

export interface FixedFooterProps {
  buttons: ActionButton[];
  statusText?: string;
  isSubmitting?: boolean;
  sx?: object;
  isPlanPage?: boolean;
  isPlanDetailPage?: boolean;
  hasNavigationPanel?: boolean;
}

const ButtonSection: React.FC<FixedFooterProps> = ({ buttons, statusText, isSubmitting }) => {
  return (
    <>
      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {buttons.map((button, index) => (
          <Button
            key={`${button.label}-${index}`}
            variant={button.variant || 'outlined'}
            color={button.color || 'primary'}
            size='large'
            startIcon={button.startIcon}
            onClick={button.onClick}
            disabled={button.disabled || button.loading}
            data-allow-navigation={button.allowNavigation !== false ? 'true' : 'false'}
            sx={{
              minWidth: 120,
              borderRadius: '100px',
              height: '56px',
              fontSize: '16px',
              ...button.sx,
            }}
          >
            {button.label}
          </Button>
        ))}
      </Box>

      {/* Status Text */}
      <Box sx={{ flex: 1, ml: 3, float: 'right' }}>
        {statusText && (
          <Typography variant='body2' color='text.secondary'>
            {statusText}
          </Typography>
        )}
        {isSubmitting && (
          <Typography variant='body2' color='text.secondary'>
            処理中...
          </Typography>
        )}
      </Box>
    </>
  );
};

export const FixedFooter: React.FC<FixedFooterProps> = ({
  buttons,
  statusText,
  isSubmitting = false,
  sx = {},
  isPlanPage = false,
  isPlanDetailPage = false,
  hasNavigationPanel = true,
}) => {
  return (
    <Paper
      elevation={3}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 10,
        p: 2,
        backgroundColor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        borderRadius: 0,
        ...sx,
      }}
    >
      {isPlanPage ? (
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 3 }}>
          {isPlanDetailPage && <CalendarBoxStyles />}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ButtonSection buttons={buttons} statusText={statusText} isSubmitting={isSubmitting} />
          </Box>
        </Box>
      ) : (
        <Container maxWidth='lg'>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <ButtonSection
                buttons={buttons}
                statusText={statusText}
                isSubmitting={isSubmitting}
              />
            </Box>
            {hasNavigationPanel && <Box sx={{ width: 250, flexShrink: 0 }}></Box>}
          </Box>
        </Container>
      )}
    </Paper>
  );
};

export default FixedFooter;
